<script lang="ts" setup>
import { useLessonRoomStore } from '@stores/lesson-room'

import { useClassroomDevices } from '@hooks/classroom/devices.hook'
import { useLessonRoomAnalytics } from '@hooks/lesson-room/lesson-room-analytics.hook'

import LessonRoomPopup from '@Molecules/LessonRoom/LessonRoomPopup.vue'
import LessonRoomDeviceSelect from '@Molecules/LessonRoom/LessonRoomDeviceSelect.vue'
import LessonRoomMicLevel from '@Molecules/LessonRoom/LessonRoomMicLevel.vue'
import LessonRoomDevicesPermissions from '@Molecules/LessonRoom/LessonRoomDevicesPermissions.vue'

defineOptions({
  name: 'LessonRoomSettingsDrawer',
})

const lessonRoomStore = useLessonRoomStore()
const analytics = useLessonRoomAnalytics()
const {
  cameras,
  microphones,
  speakers,
  selectedCameraId,
  selectedMicId,
  selectedSpeakerId,
  hasPermissions,
  loadDevices,
  subscribeDevices,
  requestPermissions,
  selectCamera,
  selectMicrophone,
  selectSpeaker,
} = useClassroomDevices()

let unsubscribe: (() => void) | null = null

const close = (): void => lessonRoomStore.setSettingsOpen(false)

const isRequestingPermissions = ref(false)

const handleAllowPermissions = async (): Promise<void> => {
  isRequestingPermissions.value = true

  try {
    await requestPermissions()
    await loadDevices()
  } finally {
    isRequestingPermissions.value = false
  }
}

const handleCameraChange = (id: string): void => {
  if (!id) return
  analytics.trackSelectCamera(id)
  void selectCamera(id)
}

const handleMicChange = (id: string): void => {
  if (!id) return
  analytics.trackSelectMicro(id)
  void selectMicrophone(id)
}

const handleSpeakerChange = (id: string): void => {
  if (!id) return
  analytics.trackSelectSpeaker(id)
  void selectSpeaker(id)
}

watch(() => lessonRoomStore.isSettingsOpen, (open) => {
  if (open) {
    void loadDevices()
    unsubscribe = subscribeDevices()
  } else {
    unsubscribe?.()
    unsubscribe = null
  }
})

onUnmounted(() => {
  unsubscribe?.()
  unsubscribe = null
})
</script>

<template>
  <LessonRoomPopup
    v-if="lessonRoomStore.isSettingsOpen"
    title="Device settings"
    align="left"
    @close="close"
  >
    <div class="lesson-room-settings">
      <LessonRoomDeviceSelect
        v-if="cameras.length"
        label="Camera"
        icon="video"
        :options="cameras"
        :model-value="selectedCameraId"
        @update:model-value="handleCameraChange"
      />
      <LessonRoomDeviceSelect
        v-if="microphones.length"
        label="Microphone"
        icon="microphone"
        :options="microphones"
        :model-value="selectedMicId"
        @update:model-value="handleMicChange"
      />
      <LessonRoomMicLevel v-if="microphones.length" />
      <LessonRoomDeviceSelect
        v-if="speakers.length"
        label="Speaker"
        icon="volume-high"
        :options="speakers"
        :model-value="selectedSpeakerId"
        @update:model-value="handleSpeakerChange"
      />
      <LessonRoomDevicesPermissions
        v-if="!hasPermissions"
        :loading="isRequestingPermissions"
        @click:allow="handleAllowPermissions"
      />
    </div>
  </LessonRoomPopup>
</template>

<style scoped lang="scss" src="@scss/components/Blocks/LessonRoom/LessonRoomSettingsDrawer.scss" />
